import { NotFoundError } from '@cian/peperrors/shared';

import {
  ATTRIBUTE_IMAGE_VERSION,
  ATTRIBUTE_INSTANCE_ID,
  ATTRIBUTE_MICROFRONTEND_NAME,
  ATTRIBUTE_PARAMETERS,
  ATTRIBUTE_RUNTIME_NAME,
  WINDOW_REGISTRY_CONST,
} from '../../../shared/constants/index.js';
import { assertIsUIRuntime } from '../../../shared/runtime/index.js';
import { childrenReady } from '../../utils/childsReady.js';
import { CDN_CURRENT_VERSION } from '../registry/index.js';

import type { ICianMFUIAttributes } from '../../../shared/cianMFUI/index.js';
import type { ICianMFUIElement } from '../../cianMFUI/index.js';
import type { IUIInstance } from '../../instance/index.js';
import type { IUIRuntime } from '../../runtime/index.js';

const REQUIRED_ATTRIBUTES: (keyof ICianMFUIAttributes)[] = [
  ATTRIBUTE_MICROFRONTEND_NAME,
  ATTRIBUTE_IMAGE_VERSION,
  ATTRIBUTE_RUNTIME_NAME,
];
const RECONNECT_ATTRIBUTES: (keyof ICianMFUIAttributes)[] = [...REQUIRED_ATTRIBUTES, ATTRIBUTE_INSTANCE_ID];
const RESERVED_ATTRIBUTES: (keyof ICianMFUIAttributes)[] = [...RECONNECT_ATTRIBUTES, ATTRIBUTE_PARAMETERS];

export class CianMFUIElement extends HTMLElement implements ICianMFUIElement {
  private runtime: IUIRuntime;
  private _instance: IUIInstance | undefined;

  private isUpdatePending = false;
  private enableUpdating() {}
  private updatePromise: Promise<void>;

  public constructor() {
    super();
    this.initialize();
  }

  public static get observedAttributes(): (keyof ICianMFUIAttributes)[] {
    return RESERVED_ATTRIBUTES;
  }

  public get instance(): IUIInstance | undefined {
    return this._instance;
  }

  public async connectedCallback() {
    const runtimePromise = this.runtimeEnsure();

    await childrenReady(this);

    this.runtime = await runtimePromise;
    this.enableUpdating();
  }

  public attributeChangedCallback(name: keyof ICianMFUIAttributes, oldValue: string | null, newValue: string | null) {
    if (!this.isConnected) {
      return;
    }

    if (RECONNECT_ATTRIBUTES.includes(name)) {
      return this.requestUpdate();
    }

    if (this._instance) {
      const { customElement } = this._instance;

      if (newValue === null) {
        return customElement.removeAttribute(name);
      }

      customElement.setAttribute(name, newValue);
    }
  }

  private get reservedAttributes(): ICianMFUIAttributes {
    const attributes = Object.fromEntries(
      RESERVED_ATTRIBUTES.map<[keyof ICianMFUIAttributes, string | null]>(attributeName => [
        attributeName,
        this.getAttribute(attributeName),
      ]).filter((attribute): attribute is [keyof ICianMFUIAttributes, string] => attribute[1] !== null),
    ) as Partial<ICianMFUIAttributes>;

    assertRequiredAttributesSet(attributes);

    return attributes;
  }

  private initialize() {
    this.updatePromise = new Promise<void>(resolve => (this.enableUpdating = resolve));
    this.requestUpdate();
  }

  private async runtimeEnsure(): Promise<IUIRuntime> {
    const { name: microfrontendName, image: imageVersion, runtime: runtimeName } = this.reservedAttributes;

    // TODO: прямое подключение к ядру реестра
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const runtime = window[WINDOW_REGISTRY_CONST]![CDN_CURRENT_VERSION].microfrontendStrict(microfrontendName)
      .imageStrict(imageVersion)
      .runtimeStrict(runtimeName);

    assertIsUIRuntime<IUIRuntime>(runtime);

    return runtime.ensure();
  }

  private requestUpdate(): void {
    this.updatePromise = this.enqueueUpdate();
  }

  private async enqueueUpdate(): Promise<void> {
    this.isUpdatePending = true;

    await this.updatePromise;

    this.performUpdate();
  }

  private performUpdate() {
    if (!this.isUpdatePending) {
      return;
    }

    this.update();
  }

  private update() {
    const { instance: instanceId, parameters } = this.reservedAttributes;

    const instance = instanceId ? this.runtime.instanceStrict(instanceId) : this.runtime.newInstance();

    if (this._instance === instance) {
      return;
    } else {
      this._instance = instance;
      this.setAttribute(ATTRIBUTE_INSTANCE_ID, instance.id);
    }

    if (
      this.children.length !== 1 ||
      !(this.children[0] instanceof (this.runtime.customElementConstructor as CustomElementConstructor)) ||
      this.children[0].getAttribute(ATTRIBUTE_INSTANCE_ID) !== instance.id
    ) {
      this.innerHTML = '';
      this.appendChild(instance.customElement);
    }

    if (parameters) {
      instance.customElement.setAttribute(ATTRIBUTE_PARAMETERS, parameters);
    }

    this.isUpdatePending = false;
  }
}

function assertRequiredAttributesSet(
  attributes: Partial<ICianMFUIAttributes>,
): asserts attributes is ICianMFUIAttributes {
  const undefinedAttributeNames = REQUIRED_ATTRIBUTES.filter(attributeName => !attributes[attributeName]);
  if (undefinedAttributeNames.length > 0) {
    throw new NotFoundError({
      message: `Отсутствует один или несколько обязательных параметров: ${undefinedAttributeNames.join(', ')}`,
      domain: 'mf-registry.cdn.cian-mf-ui',
    });
  }
}
